import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createAction } from "@reduxjs/toolkit";
import { Plus, ShieldCheck, Trash2 } from "lucide-react";
import store from "../../app/store";
import { PopupContext, TableContext } from "../../index";
import { InfoBoxes } from "../../info-boxes/InfoBoxes";
import { withMainLayout } from "../../view/LayoutHOCs";
import * as layoutService from "../../view/layoutService";
import SearchInputV2 from "../../view/SearchInputV2";
import PaginationManager from "../../view/PaginationManager";
import * as urlService from "../../view/urlService";
import * as deviceService from "../deviceService";
import {
    deleteAuthorization,
    listAuthorizationsPageable,
    saveAuthorization,
    updateAuthorization
} from "./deviceAuthorizationService";
import { ACTION_DEVICE_AUTHORIZATIONS_CHANGED } from "./DeviceAuthorizationPageReducer";
import DeviceAuthorizationModal from "./DeviceAuthorizationModal";
import DeviceAuthorizationTable from "./DeviceAuthorizationTable";
import { showSuccessToast } from "../../validation/ToastContainer";
import { getDeletePopup } from "../../view/popup/PopupContainer";

const breadcrumbs = [
    { text: "Home", redirectTo: "/", iconClassName: "icon-home" },
    { text: "Devices", redirectTo: "/devices" },
    { text: "Device Authorizations", redirectTo: "/device-authorizations" }
];

function DeviceAuthorizationPage(props) {
    const { setPopup } = React.useContext(PopupContext);
    const routeDeviceId = props.match.params.deviceId;
    const [devices, setDevices] = useState([]);
    const [showRuleModal, setShowRuleModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [tableConfig, setTableConfig] = useState({
        selected: [],
        isLoading: false,
        lastUpdated: Date.now(),
        searchTerm: ""
    });

    useEffect(() => {
        layoutService.setPageTitle("Device Authorizations");
        layoutService.setBreadcrumbs(breadcrumbs);
    }, []);

    useEffect(() => {
        deviceService.list().then(setDevices);
        store.dispatch(createAction(ACTION_DEVICE_AUTHORIZATIONS_CHANGED)({ authorizations: [] }));
    }, []);

    const refresh = () => {
        const pagination = urlService.getPagination(true);
        if (Number(pagination.page || 0) !== 0) {
            props.history.push({
                pathname: props.history.location.pathname,
                search: urlService.updatePagination(0, pagination.size || 10)
            });
        }

        setTableConfig(current => ({
            ...current,
            selected: [],
            pageNumber: 0,
            lastUpdated: Date.now()
        }));
    };

    const openAddRule = () => {
        setEditingRule(null);
        setShowRuleModal(true);
    };

    const openEditRule = (rule) => {
        setEditingRule(rule);
        setShowRuleModal(true);
    };

    const onSaveRule = async (rule) => {
        const selectedActions = rule.actions?.length ? rule.actions : [rule.action];
        const selectedUsers = Array.from(new Set((rule.usernames?.length ? rule.usernames : [rule.username])
            .map(username => String(username || "").trim())
            .filter(Boolean)));
        const rulesToSave = selectedUsers.flatMap(username =>
            selectedActions.map(action => ({
                ...rule,
                username,
                userId: null,
                action,
                authorization: rule.actionAuthorizations?.[action] || rule.authorization || "ALLOWED"
            }))
        );

        if (rule.id) {
            const [firstRule, ...extraRules] = rulesToSave;
            await updateAuthorization({ ...firstRule, id: rule.id });
            await Promise.all(extraRules.map(item => saveAuthorization({ ...item, id: undefined })));
            showSuccessToast(
                extraRules.length ? "Authorization was updated and extra rules were saved" : "Authorization was updated",
                "Device Authorization"
            );
        } else {
            await Promise.all(rulesToSave.map(item => saveAuthorization(item)));
            showSuccessToast(
                rulesToSave.length > 1 ? `${rulesToSave.length} authorizations were saved` : "Authorization was saved",
                "Device Authorization"
            );
        }
        setShowRuleModal(false);
        refresh();
    };

    const onDeleteSelectedRules = async () => {
        const selected = tableConfig.selected || [];
        if (!selected.length) return;

        setPopup(
            getDeletePopup(
                `You are about to delete ${selected.length} selected ${selected.length > 1 ? "authorizations" : "authorization"}.`,
                "Delete Authorization",
                async () => {
                    await Promise.all(selected.map(id => deleteAuthorization(id)));
                    showSuccessToast(
                        selected.length > 1 ? "Authorizations were deleted" : "Authorization was deleted",
                        "Device Authorization"
                    );
                    refresh();
                },
                undefined,
                selected.length > 1 ? "Delete Authorizations" : "Delete Authorization"
            )
        );
    };

    const handleSearch = (event) => {
        setTableConfig(current => ({ ...current, searchTerm: event.target.value }));
    };

    const getAuthorizationsPage = (pageNumber, pageSize) => listAuthorizationsPageable(pageNumber, pageSize, routeDeviceId);
    const selectedCount = tableConfig.selected?.length || 0;
    const isDeleteSelectedDisabled = selectedCount === 0;

    return (
        <TableContext.Provider value={{ config: tableConfig, setConfig: setTableConfig }}>
            <InfoBoxes />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card oct_analysis-card-v2">
                            <div className="oct_analysis-header-v2 mt-0">
                                <div className="oct_analysis-title-section">
                                    <div className="d-flex align-items-center">
                                        <ShieldCheck className="oct_icon mr-2" />
                                        <h2>DEVICE AUTHORIZATIONS</h2>
                                    </div>
                                </div>
                                <div className="oct_analysis-actions">
                                    <button
                                        type="button"
                                        className="oct_btn-danger-v2"
                                        disabled={isDeleteSelectedDisabled}
                                        onClick={onDeleteSelectedRules}
                                    >
                                        <Trash2 className="oct_icon" />
                                        Delete Selected ({selectedCount})
                                    </button>
                                    <button type="button" className="oct_btn-export-v2" onClick={openAddRule}>
                                        <Plus className="oct_icon" />
                                        Add Authorization
                                    </button>
                                </div>
                            </div>

                            <div className="oct_filter-section-v2 reports-toolbar-filter">
                                <SearchInputV2
                                    value={tableConfig.searchTerm}
                                    onChange={handleSearch}
                                    placeholder="Search by user or authorization"
                                    className="m-0"
                                />
                            </div>

                            <div className="card-body">
                                <DeviceAuthorizationTable onEdit={openEditRule} />
                                <PaginationManager key="device-authorization-rules" getPage={getAuthorizationsPage} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showRuleModal && (
                <DeviceAuthorizationModal
                    devices={devices}
                    currentDeviceId={routeDeviceId}
                    initialData={editingRule}
                    onClose={() => setShowRuleModal(false)}
                    onSave={onSaveRule}
                />
            )}
        </TableContext.Provider>
    );
}

export default withMainLayout(connect()(DeviceAuthorizationPage));
